export type ParticipantDiffRow = {
  billing_tin?: string | null;
  ccn?: string | null;
  legal_business_name?: string | null;
  participant_type?: string | null;
  individual_npi?: string | null;
  billing_tin_current_status?: string | null;
  effective_end_date?: string | null;
  last_updated?: string | null;
  performance_year?: string | null;
};

export type ParticipantDiffPractice = {
  practice_id: string;
  practice_name: string;
  legal_business_name?: string | null;
  tin?: string | null;
  ccn?: string | null;
  is_archived: boolean;
  primary_network_id?: string | null;
};

export type ParticipantDiffNetwork = {
  network_id: string;
  network_number: number;
  network_name: string;
  is_archived: boolean;
  practices: ParticipantDiffPractice[];
};

export type ParticipantPracticeCreate = {
  practice_name: string;
  legal_business_name: string;
  tin: string;
  ccn?: string;
  primary_network_id: string;
};

export type ParticipantPracticeDiff = {
  archives: string[];
  reinstates: string[];
  creates: ParticipantPracticeCreate[];
  skipped_no_tin: number;
  error?: string;
};

export const normalizeTin = (tin?: string | null) => {
  const digits = String(tin || '').replace(/\D/g, '');
  if (!digits) return undefined;
  return digits.length < 9 ? digits.padStart(9, '0') : digits;
};

const participantProviderKey = (row: ParticipantDiffRow) => {
  const providerType = row.participant_type || '';
  if (providerType.includes('Organizational')) {
    return { kind: 'org', identity: (row.legal_business_name || '').trim() };
  }
  if (providerType.includes('Facility') || row.ccn) {
    return { kind: 'fac', identity: (row.ccn || '').trim() };
  }
  return {
    kind: 'ind',
    identity: (row.individual_npi || row.legal_business_name || '').trim(),
  };
};

const latestStateRows = (rows: ParticipantDiffRow[]) => {
  const dated = rows.filter((row) => row.last_updated);
  if (!dated.length) return rows;
  const latest = dated.reduce(
    (current, row) =>
      (row.last_updated || '') > current ? row.last_updated || '' : current,
    ''
  );
  return dated.filter((row) => row.last_updated === latest);
};

const providerIsActive = (rows: ParticipantDiffRow[], today: string) =>
  latestStateRows(rows).some((row) => {
    // New exports omit provider type. Also recognize the labels saved by the
    // initial importer so previously uploaded files still work.
    const isTinExport =
      !row.participant_type || row.participant_type.startsWith('Participant ');
    const approved =
      row.billing_tin_current_status === 'Approved' ||
      (isTinExport && row.billing_tin_current_status === 'Pre-approved');
    // The TIN/CCN/iNPI export provides a performance year instead of dates.
    const endDate =
      row.effective_end_date ||
      (isTinExport && /^\d{4}$/.test(row.performance_year || '')
        ? `${Number(row.performance_year) + 1}-01-01`
        : null);
    return Boolean(approved && endDate && endDate.slice(0, 10) > today);
  });

const pickDisplayName = (rows: ParticipantDiffRow[]) => {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const name = (row.legal_business_name || '').trim();
    if (name) counts.set(name, (counts.get(name) || 0) + 1);
  });
  const sortedNames = Array.from(counts.entries()).sort(
    ([nameA, countA], [nameB, countB]) =>
      countB - countA || nameA.localeCompare(nameB)
  );
  return sortedNames.length ? sortedNames[0][0] : undefined;
};

export const computeParticipantPracticeDiff = (
  participantRows: ParticipantDiffRow[],
  networks: ParticipantDiffNetwork[],
  today = new Date().toISOString().slice(0, 10)
): ParticipantPracticeDiff => {
  const providersByTin = new Map<string, Map<string, ParticipantDiffRow[]>>();
  participantRows.forEach((row) => {
    const tin = normalizeTin(row.billing_tin);
    if (!tin) return;
    const { kind, identity } = participantProviderKey(row);
    const providers = providersByTin.get(tin) || new Map();
    const key = [kind, identity].join(':');
    providers.set(key, [...(providers.get(key) || []), row]);
    providersByTin.set(tin, providers);
  });

  const rosterTins = new Set<string>();
  const rosterPairs = new Set<string>();
  const namesByTin = new Map<string, string>();
  const namesByPair = new Map<string, string>();

  providersByTin.forEach((providers, tin) => {
    const orgEntries = Array.from(providers.entries()).filter(([key]) =>
      key.startsWith('org:')
    );
    const activeOrgEntries = orgEntries.filter(([, rows]) =>
      providerIsActive(rows, today)
    );
    if (orgEntries.length) {
      if (activeOrgEntries.length) {
        rosterTins.add(tin);
        namesByTin.set(
          tin,
          activeOrgEntries
            .map(([key]) => key.slice(4))
            .sort((a, b) => a.localeCompare(b))[0]
        );
      }
    } else {
      const activeIndividualRows = Array.from(providers.entries())
        .filter(
          ([key, rows]) =>
            key.startsWith('ind:') && providerIsActive(rows, today)
        )
        .reduce<ParticipantDiffRow[]>(
          (allRows, [, rows]) => [...allRows, ...latestStateRows(rows)],
          []
        );
      if (activeIndividualRows.length) {
        rosterTins.add(tin);
        const name = pickDisplayName(activeIndividualRows);
        if (name) namesByTin.set(tin, name);
      }
    }

    Array.from(providers.entries()).forEach(([key, rows]) => {
      if (!key.startsWith('fac:') || key === 'fac:') return;
      if (!providerIsActive(rows, today)) return;
      const pair = [tin, key.slice(4)].join(':');
      rosterPairs.add(pair);
      const name = pickDisplayName(latestStateRows(rows));
      if (name) namesByPair.set(pair, name);
    });
  });

  const archives: string[] = [];
  const coveredTins = new Set<string>();
  const coveredPairs = new Set<string>();
  const archivedByTin = new Map<string, string>();
  const archivedByPair = new Map<string, string>();
  let skippedNoTin = 0;

  networks.forEach((network) => {
    network.practices.forEach((practice) => {
      const tin = normalizeTin(practice.tin);
      const ccn = (practice.ccn || '').trim() || undefined;
      if (practice.is_archived) {
        if (tin && ccn && !archivedByPair.has([tin, ccn].join(':'))) {
          archivedByPair.set([tin, ccn].join(':'), practice.practice_id);
        } else if (tin && !archivedByTin.has(tin)) {
          archivedByTin.set(tin, practice.practice_id);
        }
        return;
      }
      if (!tin) {
        skippedNoTin += 1;
        return;
      }
      if (ccn) {
        const pair = [tin, ccn].join(':');
        coveredPairs.add(pair);
        if (!rosterPairs.has(pair)) archives.push(practice.practice_id);
      } else {
        coveredTins.add(tin);
        if (!rosterTins.has(tin)) archives.push(practice.practice_id);
      }
    });
  });

  const activeNetworks = networks.filter((network) => !network.is_archived);
  if (!activeNetworks.length) {
    return {
      archives: [],
      reinstates: [],
      creates: [],
      skipped_no_tin: skippedNoTin,
      error: 'No active network exists for this ACO ID',
    };
  }
  const targetNetwork = [...activeNetworks].sort(
    (a, b) => a.network_number - b.network_number
  )[0];

  const reinstates: string[] = [];
  const creates: ParticipantPracticeCreate[] = [];
  Array.from(rosterTins)
    .filter((tin) => !coveredTins.has(tin))
    .sort()
    .forEach((tin) => {
      const archivedPracticeId = archivedByTin.get(tin);
      if (archivedPracticeId) {
        reinstates.push(archivedPracticeId);
        return;
      }
      const name = namesByTin.get(tin) || 'TIN ' + tin;
      creates.push({
        practice_name: name,
        legal_business_name: name,
        tin,
        primary_network_id: targetNetwork.network_id,
      });
    });
  Array.from(rosterPairs)
    .filter((pair) => !coveredPairs.has(pair))
    .sort()
    .forEach((pair) => {
      const archivedPracticeId = archivedByPair.get(pair);
      if (archivedPracticeId) {
        reinstates.push(archivedPracticeId);
        return;
      }
      const separator = pair.indexOf(':');
      const tin = pair.slice(0, separator);
      const ccn = pair.slice(separator + 1);
      const name = namesByPair.get(pair) || 'TIN ' + tin;
      creates.push({
        practice_name: name,
        legal_business_name: name,
        tin,
        ccn,
        primary_network_id: targetNetwork.network_id,
      });
    });

  return {
    archives: archives.sort(),
    reinstates: reinstates.sort(),
    creates,
    skipped_no_tin: skippedNoTin,
  };
};
